import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import {
    SettingsCategory,
    SettingsVariable,
    getAffectedPolicies,
    removeVariable,
    updateVariable,
} from '../../../services/api/departmentVariablesService';
import {
    DepartmentUser,
    categoryTitle,
    isLabelTaken,
    variableLabel,
} from '../utils/settingsView';
import { useToast } from '../../../hooks/use-toast';
import { Button, buttonVariants } from '../../ui/button';
import { Input } from '../../ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';
import { VariableRowLabel } from './VariableRowLabel';
import { RefVariablePicker } from './RefVariablePicker';
import { AffectedPoliciesList } from './AffectedPoliciesList';
import {
    VariableDraft,
    buildUpdateInput,
    canSaveVariable,
    displayValue,
    hasValueChange,
} from './variableRowUtils';

interface VariableRowProps {
    variable: SettingsVariable;
    allVariables: SettingsVariable[];
    tabCategories: SettingsCategory[];
    users: DepartmentUser[];
    onSaved: () => Promise<void>;
}

export default function VariableRow({
    variable,
    allVariables,
    tabCategories,
    users,
    onSaved,
}: VariableRowProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState<VariableDraft>({
        value: '',
        label: '',
        category: variable.category,
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteBlocked, setDeleteBlocked] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [inUse, setInUse] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const label = variableLabel(variable, t);
    const value = displayValue(variable, users);
    const canSave = isEditing && canSaveVariable(variable, draft, label, inUse);
    const categoryOptions = tabCategories.filter(
        (category) =>
            category.kind !== 'other' || category.key === variable.category
    );
    const userOptions = useMemo(
        () =>
            users.map((user) => ({
                id: user.id,
                label: user.name,
                description: user.role,
            })),
        [users]
    );

    const enterEdit = async () => {
        if (saving) return;
        setDraft({
            value: variable.fixedValue || '',
            refId: variable.refId,
            label,
            category: variable.category,
        });
        setIsEditing(true);
        if (!variable.stored) return;
        try {
            const policies = await getAffectedPolicies(variable.name);
            setInUse(policies.length > 0);
        } catch {
            setInUse(false);
        }
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setInUse(false);
    };

    useEffect(() => {
        if (!isEditing || variable.field === 'ref') return;
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [isEditing, variable.field]);

    const checkLabel = () => {
        if (!isLabelTaken(allVariables, draft.label, t, variable.name)) {
            return true;
        }
        toast({
            title: t('departmentVariables.variableRow.labelExists'),
            variant: 'destructive',
        });
        return false;
    };

    const saveField = async () => {
        if (!canSave || saving || !checkLabel()) return;
        setSaving(true);
        try {
            await updateVariable(
                variable.name,
                buildUpdateInput(variable, draft, label)
            );
            await onSaved();
            setIsEditing(false);
        } catch {
            return;
        } finally {
            setSaving(false);
        }
    };

    const deleteVariable = async () => {
        try {
            await removeVariable(variable.name);
            await onSaved();
        } catch {
            return;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            cancelEdit();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            void saveField();
        }
    };

    const renderEditableField = () => {
        if (variable.field === 'ref') {
            return (
                <RefVariablePicker
                    options={userOptions}
                    selectedId={draft.refId}
                    freeTextValue={draft.value}
                    canClear={!inUse}
                    placeholder={t(
                        'departmentVariables.variableRow.selectOrTypeName'
                    )}
                    searchPlaceholder={t(
                        'departmentVariables.variableRow.searchTeamOrType'
                    )}
                    emptyText={t(
                        'departmentVariables.variableRow.noTeamMembersFound'
                    )}
                    onSelect={(id) =>
                        setDraft((prev) => ({ ...prev, refId: id, value: '' }))
                    }
                    onSelectFreeText={(text) =>
                        setDraft((prev) => ({
                            ...prev,
                            refId: undefined,
                            value: text,
                        }))
                    }
                    onClear={() =>
                        setDraft((prev) => ({
                            ...prev,
                            refId: undefined,
                            value: '',
                        }))
                    }
                />
            );
        }

        return (
            <Input
                ref={inputRef}
                type={variable.field === 'number' ? 'number' : 'text'}
                min={variable.field === 'number' ? 1 : undefined}
                maxLength={variable.field === 'number' ? undefined : 250}
                placeholder={t(
                    `departmentVariables.variables.${variable.name}.placeholder`,
                    ''
                )}
                value={draft.value}
                onChange={(e) =>
                    setDraft((prev) => ({ ...prev, value: e.target.value }))
                }
                onKeyDown={handleKeyDown}
            />
        );
    };

    if (isEditing) {
        return (
            <div className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-b border-subtle last:border-0 items-start">
                <VariableRowLabel
                    label={label}
                    isCustom={variable.renamable}
                    editLabel={draft.label}
                    onEditLabel={(val: string) =>
                        setDraft((prev) => ({ ...prev, label: val }))
                    }
                    disabled={inUse}
                    disabledHint={t(
                        'departmentVariables.variableRow.usedByPolicies'
                    )}
                    align="start"
                />
                <div className="space-y-2">
                    {renderEditableField()}
                    {!variable.fixed && (
                        <Select
                            value={draft.category}
                            onValueChange={(category) =>
                                setDraft((prev) => ({ ...prev, category }))
                            }
                        >
                            <SelectTrigger
                                aria-label={t(
                                    'departmentVariables.variableRow.category'
                                )}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {categoryOptions.map((category) => (
                                    <SelectItem
                                        key={category.key}
                                        value={category.key}
                                    >
                                        {categoryTitle(category, t)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <div className="flex justify-end gap-2">
                        {!variable.fixed && (
                            <>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    disabled={saving}
                                    onClick={() => {
                                        setDeleteBlocked(false);
                                        setDeleteDialogOpen(true);
                                    }}
                                >
                                    {t(
                                        'departmentVariables.variableRow.delete'
                                    )}
                                </Button>
                                <AlertDialog
                                    open={deleteDialogOpen}
                                    onOpenChange={setDeleteDialogOpen}
                                >
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                {t(
                                                    'departmentVariables.variableRow.deleteTitle',
                                                    { label }
                                                )}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {deleteBlocked
                                                    ? t(
                                                          'departmentVariables.variableRow.deleteBlocked',
                                                          { label }
                                                      )
                                                    : t(
                                                          'departmentVariables.variableRow.deleteConfirm',
                                                          { label }
                                                      )}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AffectedPoliciesList
                                            variableName={variable.name}
                                            onLoaded={(policies) =>
                                                setDeleteBlocked(
                                                    policies.length > 0
                                                )
                                            }
                                        />
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>
                                                {t(
                                                    'departmentVariables.variableRow.cancel'
                                                )}
                                            </AlertDialogCancel>
                                            {!deleteBlocked && (
                                                <AlertDialogAction
                                                    className={buttonVariants({
                                                        variant: 'destructive',
                                                    })}
                                                    onClick={() =>
                                                        void deleteVariable()
                                                    }
                                                >
                                                    {t(
                                                        'departmentVariables.variableRow.delete'
                                                    )}
                                                </AlertDialogAction>
                                            )}
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={saving}
                        >
                            {t('departmentVariables.variableRow.cancel')}
                        </Button>
                        {variable.stored ? (
                            <>
                                <Button
                                    size="sm"
                                    disabled={saving || !canSave}
                                    onClick={() => {
                                        if (!hasValueChange(variable, draft)) {
                                            void saveField();
                                            return;
                                        }
                                        if (checkLabel()) {
                                            setSaveDialogOpen(true);
                                        }
                                    }}
                                >
                                    {saving
                                        ? t(
                                              'departmentVariables.variableRow.saving'
                                          )
                                        : t(
                                              'departmentVariables.variableRow.save'
                                          )}
                                </Button>
                                <AlertDialog
                                    open={saveDialogOpen}
                                    onOpenChange={setSaveDialogOpen}
                                >
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                {t(
                                                    'departmentVariables.variableRow.updateTitle',
                                                    { label }
                                                )}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {t(
                                                    'departmentVariables.variableRow.updateDescription'
                                                )}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AffectedPoliciesList
                                            variableName={variable.name}
                                        />
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>
                                                {t(
                                                    'departmentVariables.variableRow.cancel'
                                                )}
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => void saveField()}
                                            >
                                                {t(
                                                    'departmentVariables.variableRow.update'
                                                )}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </>
                        ) : (
                            <Button
                                size="sm"
                                onClick={() => void saveField()}
                                disabled={saving || !canSave}
                            >
                                {saving
                                    ? t(
                                          'departmentVariables.variableRow.saving'
                                      )
                                    : t('departmentVariables.variableRow.save')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-b border-subtle last:border-0 items-center cursor-pointer hover:bg-accent/40 transition-colors group"
            onClick={() => void enterEdit()}
        >
            <VariableRowLabel label={label} isCustom={false} />
            <div className="flex items-center justify-end gap-2 min-w-0">
                {value ? (
                    <span className="text-sm text-foreground font-medium truncate">
                        {value}
                    </span>
                ) : (
                    <span className="text-sm text-muted-foreground/50">
                        {t('departmentVariables.variableRow.clickToSet')}
                    </span>
                )}
                <Button
                    variant="outline"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        void enterEdit();
                    }}
                >
                    <Pencil className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
}
